import Konva from 'konva';
import { loadImage, Image } from 'canvas';
import { logger } from '@icc/shared';

export async function  loadKonvaImage(source: string, host: string | null):Promise<Image> {

    return new Promise<Image>((resolve,reject) => {
        if(host && source){
            const isBrowser = (typeof window !== 'undefined') && (typeof window?.document !== 'undefined');
            if (!isBrowser && source.endsWith('.webp')) {
                const data = source.split('/');
                const fileName = data[data.length - 1];
                source = source.replace(fileName, 'png/' + fileName + '.png');
            }
            loadImage(host+source).then((image) => {
                resolve(image);
              }).catch((err)=>{
                console.error(err);
                reject(null);
              })
        }else{
            reject(null);
        }
    });
}

export async function getPhotoImg(photo: any, loadedPhotos: Record<string, Image>, host:string|null = null) {
    const photoSrc = photo ? '/files/photos/' + photo.img : '';
    if (loadedPhotos[photoSrc]) {
        return loadedPhotos[photoSrc];
    }
    const image = await loadKonvaImage(photoSrc, host);
    loadedPhotos[photoSrc] = image;
    return image;
}

export async function setPhoto(entity: any, loadedPhotos: any, photos: any, host:string|null = null) {
    if (photos[entity.graphicalParameters.photo.id]) {
        entity.konvaObject.fill(null);
        const image = await getPhotoImg(photos[entity.graphicalParameters.photo.id], loadedPhotos,host);
        if (image) {
            entity.konvaObject.fillPatternImage(image);
            entity.graphicalParameters.photo.width = image.width;
            entity.graphicalParameters.photo.height = image.height;
            entity.graphicalParameters.off = image.width;
            entity.graphicalParameters.photo.height = image.height;
        }
    }
}

export async function getCrossLinesImg(crossLineOption: any, loadedCrossLines: any, host:string|null = null) {
    const src = '/img/sketch/' + crossLineOption.img;
    if (loadedCrossLines[src]) {
        return loadedCrossLines[src];
    }

    const image = await loadKonvaImage(src,host);
    loadedCrossLines[src] = image;
    return image;

}

export async function setCrossLines(entity: any, loadedCrossLines: any, host:string|null = null) {
    try {
        entity.konvaObject.fill(null);
        const image = await getCrossLinesImg(entity.graphicalParameters.crossLine, loadedCrossLines, host);
        if (image) entity.konvaObject.fillPatternImage(image);
    } catch (err) {
        logger.error(err);
    }
}

export function setRepeat(entity: any) {
    const parameter = entity.graphicalParameters.repeat;
    if (parameter && parameter.repeat) {
        entity.konvaObject.fillPatternRepeat(parameter.repeat);
    }
}

export function setStretching(entity: any) {
    if (entity && ['circle', 'rectangle'].includes(entity.type)) {
        const parameter = entity?.graphicalParameters?.stretching;
        if (parameter) {
            switch (parameter.stretching) {
                case 'stretching':
                    stretchingImg(entity);
                    break;
                case 'stretching-x':
                    stretchingXImg(entity);
                    break;
                case 'stretching-y':
                    stretchingYImg(entity);
                    break;
                default:
                    noStretchingImg(entity);
            }
            entity.graphicalParameters.percentWidth = calculateImgPercentWidth(entity);
            entity.graphicalParameters.percentHeight = calculateImgPercentHeight(entity);
        }
        roundPercentWidthAndHeight(entity);
    }
}

export function setScale(entity: any, parameter: any) {
    roundPercentWidthAndHeight(entity);
    switch (parameter) {
        case 'width':
            setPercentWidth(entity);
            break;

        default:
            setPercentHeight(entity);
    }
}

export function setPosition(entity: any, parameter: any) {
    roundPositionXAndY(entity);
    switch (parameter) {
        case 'x':
            imgPositionX(entity, entity.graphicalParameters.positionX);
            break;

        default:
            imgPositionY(entity, entity.graphicalParameters.positionY);
    }
}

export function setOffset(entity: any, parameter: any) {
    roundOffsetXAndY(entity);
    switch (parameter) {
        case 'x':
            imgOffsetX(entity, entity.graphicalParameters.offsetX);
            break;

        default:
            imgOffsetY(entity, entity.graphicalParameters.offsetY);
    }
}

function imgScaleX(entity: any, scale: number) {
    if (entity.graphicalParameters.photo || entity.graphicalParameters.optionPhoto || entity.graphicalParameters.crossLine) {
        entity.konvaObject.fillPatternScaleX(entity.mirror.x ? -scale : scale);
    }
}
function imgScaleY(entity: any, scale: number) {
    if (entity.graphicalParameters.photo || entity.graphicalParameters.optionPhoto || entity.graphicalParameters.crossLine) {
        entity.konvaObject.fillPatternScaleY(entity.mirror.y ? -scale : scale);
    }
}

function imgScale(entity: any, scaleX: number, scaleY: number) {
    imgScaleX(entity, scaleX);
    imgScaleY(entity, scaleY);
}

function stretchingImg(entity: any) {

    entity.graphicalParameters.percentWidth = calculateImgPercentWidth(entity);
    entity.graphicalParameters.percentHeight = calculateImgPercentHeight(entity);

    const width = getPhotoWidth(entity, false);
    const height = getPhotoHeight(entity, false);

    if (width && height) {
        imgScale(entity, entity.konvaObject.width() / width, entity.konvaObject.height() / height);
    }
}

function stretchingXImg(entity: any) {
    const width = getPhotoWidth(entity, false);

    if (width) {
        imgScale(entity, entity.konvaObject.width() / width, computeBaseScaleHeight(entity));
    }
}

function stretchingYImg(entity: any) {
    const height = getPhotoHeight(entity, false);
    if (height) {
        imgScale(entity, computeBaseScaleWidth(entity), entity.konvaObject.height() / height);
    }
}

function noStretchingImg(entity: any) {
    entity.graphicalParameters.percentWidth = null;
    entity.graphicalParameters.percentHeight = null;
    imgScale(entity, computeBaseScaleWidth(entity), computeBaseScaleHeight(entity));
}

function roundPercentWidthAndHeight(entity: any, factor = 10) {
    entity.graphicalParameters.percentWidth =
        Math.round(entity.graphicalParameters.percentWidth * factor) / factor;
    entity.graphicalParameters.percentHeight =
        Math.round(entity.graphicalParameters.percentHeight * factor) / factor;
}

function roundPositionXAndY(entity: any) {
    entity.graphicalParameters.positionX = Math.round(entity.graphicalParameters.positionX);
    entity.graphicalParameters.positionY = Math.round(entity.graphicalParameters.positionY);
}

function roundOffsetXAndY(entity: any) {
    entity.graphicalParameters.offsetX = Math.round(entity.graphicalParameters.offsetX);
    entity.graphicalParameters.offsetY = Math.round(entity.graphicalParameters.offsetY);
}

function calculateImgPercentWidth(entity: any) {
    const baseScale = computeBaseScaleWidth(entity);
    return Math.abs(entity.konvaObject.fillPatternScaleX() / baseScale) * 100;
}

function calculateImgPercentHeight(entity: any) {
    const baseScale = computeBaseScaleHeight(entity);
    return Math.abs(entity.konvaObject.fillPatternScaleY() / baseScale) * 100;
}

function calculateImgScaleX(entity: any) {
    const baseScale = computeBaseScaleWidth(entity);
    return (entity.graphicalParameters.percentWidth / 100) * baseScale;
}

function calculateImgScaleY(entity: any) {
    const baseScale = computeBaseScaleHeight(entity);
    return (entity.graphicalParameters.percentHeight / 100) * baseScale;
}

function setPercentWidth(entity: any) {
    const scale = calculateImgScaleX(entity);
    imgScaleX(entity, scale);
}
function setPercentHeight(entity: any) {
    const scale = calculateImgScaleY(entity);
    imgScaleY(entity, scale);
}

function imgPositionX(entity: any, x: number) {
    if (['circle', 'rectangle'].includes(entity.type)) {
        if (entity.mirror.x) {
            const width = getPhotoWidth(entity);
            x = width * Math.abs(entity.konvaObject.fillPatternScaleX());
        }
        entity.konvaObject.fillPatternX(
            entity.type === 'circle' ? x - entity.konvaObject.radius() : x
        );
    }
}

function imgPositionY(entity: any, y: number) {
    if (['circle', 'rectangle'].includes(entity.type)) {
        if (entity.mirror.y) {
            const height = getPhotoHeight(entity);
            y = height * Math.abs(entity.konvaObject.fillPatternScaleY());
        }
        entity.konvaObject.fillPatternY(
            entity.type === 'circle' ? y - entity.konvaObject.radius() : y
        );
    }
}

function imgOffsetX(entity: any, offset: number) {
    entity.konvaObject.fillPatternOffsetX(offset);
}

function imgOffsetY(entity: any, offset: number) {
    entity.konvaObject.fillPatternOffsetY(offset);
}

function getPhotoWidth(entity: any, includeMM = true) {
    if (includeMM && entity?.graphicalParameters?.photo?.width_mm) {
        return entity?.graphicalParameters?.photo?.width_mm;
    } else if (entity?.graphicalParameters?.photo?.width) {
        return entity?.graphicalParameters?.photo?.width;
    } else if (includeMM && entity?.graphicalParameters?.optionPhoto?.width_mm) {
        return entity?.graphicalParameters?.optionPhoto?.width_mm;
    } else if (entity?.graphicalParameters?.optionPhoto?.width) {
        return entity?.graphicalParameters?.optionPhoto?.width;
    }
    return null;
}

function getPhotoHeight(entity: any, includeMM = true) {
    if (includeMM && entity?.graphicalParameters?.photo?.height_mm) {
        return entity?.graphicalParameters?.photo?.height_mm;
    } else if (entity?.graphicalParameters?.photo?.height) {
        return entity?.graphicalParameters?.photo?.height;
    } else if (includeMM && entity?.graphicalParameters?.optionPhoto?.height_mm) {
        return entity?.graphicalParameters?.optionPhoto?.height_mm;
    } else if (entity?.graphicalParameters?.optionPhoto?.height) {
        return entity?.graphicalParameters?.optionPhoto?.height;
    }
    return null;
}

function computeBaseScaleWidth(entity: any) {
    const width = getPhotoWidth(entity, false);
    const widthMM = getPhotoWidth(entity);
    if (width !== widthMM) {
        return widthMM / width;
    } else {
        return 1;
    }
}
function computeBaseScaleHeight(entity: any) {
    const height = getPhotoHeight(entity, false);
    const heightMM = getPhotoHeight(entity);
    if (height !== heightMM) {
        return heightMM / height;
    } else {
        return 1;
    }
}
